import { WORK_ITEMS, type WorkItem } from "@/lib/content/work";
import {
  NEWSLETTER_NUMBERED_SERIES,
  NEWSLETTER_SERIES_RECAP,
} from "./newsletter-project-codes";

/** Canonical work category slugs, display labels, and code letters. */
export type WorkCategorySlug =
  | "digital-experiences"
  | "medical-devices"
  | "connected-systems"
  | "connected-devices"
  | "ai-consulting"
  | "marketing-websites"
  | "real-estate-architecture"
  | "mobile-apps"
  | "product-design"
  | "wizard"
  | "coming-soon";

export type WorkCategoryDefinition = {
  slug: WorkCategorySlug;
  label: string;
  codeLetter: string;
  /**
   * Serial categories drive two-char project codes (`0D`, `1M`).
   * Non-serial categories expose their letter as tag codes only (`W`, `R`, `H`).
   */
  serial: boolean;
};

/** Single source of truth — used by project codes, Work API meta, and inventory tags. */
export const WORK_CATEGORIES: readonly WorkCategoryDefinition[] = [
  {
    slug: "digital-experiences",
    label: "Digital experiences",
    codeLetter: "D",
    serial: true,
  },
  {
    slug: "medical-devices",
    label: "Medical devices",
    codeLetter: "M",
    serial: true,
  },
  {
    slug: "connected-systems",
    label: "Connected systems",
    codeLetter: "C",
    serial: true,
  },
  {
    slug: "connected-devices",
    label: "Connected devices",
    codeLetter: "V",
    serial: false,
  },
  {
    slug: "ai-consulting",
    label: "AI consulting",
    codeLetter: "A",
    serial: true,
  },
  {
    slug: "marketing-websites",
    label: "Marketing websites",
    codeLetter: "W",
    serial: false,
  },
  {
    slug: "real-estate-architecture",
    label: "Real estate & architecture",
    codeLetter: "R",
    serial: false,
  },
  {
    slug: "mobile-apps",
    label: "Mobile apps",
    codeLetter: "P",
    serial: false,
  },
  {
    slug: "product-design",
    label: "Product design",
    codeLetter: "G",
    serial: false,
  },
  {
    slug: "wizard",
    label: "Wizard",
    codeLetter: "Z",
    serial: false,
  },
  {
    slug: "coming-soon",
    label: "Coming soon",
    codeLetter: "X",
    serial: true,
  },
] as const;

export const COMING_SOON_CATEGORY_SLUG: WorkCategorySlug = "coming-soon";

/** `coming-soon` is always last — used for API output, tags, and serial letter priority. */
export function workCategoriesOrdered(
  categories: readonly string[] | undefined,
): WorkCategorySlug[] {
  if (!categories?.length) {
    return [];
  }

  const rest: WorkCategorySlug[] = [];
  let hasComingSoon = false;

  for (const slug of categories) {
    if (slug === COMING_SOON_CATEGORY_SLUG) {
      hasComingSoon = true;
    } else {
      rest.push(slug as WorkCategorySlug);
    }
  }

  return hasComingSoon ? [...rest, COMING_SOON_CATEGORY_SLUG] : rest;
}

export const WORK_CATEGORY_BY_SLUG: Readonly<
  Record<WorkCategorySlug, WorkCategoryDefinition>
> = Object.fromEntries(
  WORK_CATEGORIES.map((entry) => [entry.slug, entry]),
) as Record<WorkCategorySlug, WorkCategoryDefinition>;

export const WORK_CATEGORY_LABELS: Readonly<Record<WorkCategorySlug, string>> =
  Object.fromEntries(
    WORK_CATEGORIES.map((entry) => [entry.slug, entry.label]),
  ) as Record<WorkCategorySlug, string>;

/** All category slug → code letter (serial and tag). */
export const WORK_CATEGORY_CODE_LETTER: Readonly<Record<string, string>> =
  Object.fromEntries(
    WORK_CATEGORIES.map((entry) => [entry.slug, entry.codeLetter]),
  );

/** Serial category slug → code letter — used for `0D`-style project codes only. */
export const WORK_SERIAL_CATEGORY_CODE_LETTER: Readonly<Record<string, string>> =
  Object.fromEntries(
    WORK_CATEGORIES.filter((entry) => entry.serial).map((entry) => [
      entry.slug,
      entry.codeLetter,
    ]),
  );

export function workCategoryLabel(slug: string): string {
  return (
    WORK_CATEGORY_LABELS[slug as WorkCategorySlug] ??
    slug.replace(/-/g, " ")
  );
}

/** Inventory / filter pill — every category shows its code letter. */
export function workCategoryTagLabel(slug: string): string {
  const def = WORK_CATEGORY_BY_SLUG[slug as WorkCategorySlug];
  if (!def) {
    return workCategoryLabel(slug);
  }
  return `${def.label} (${def.codeLetter})`;
}

export function workSerialCategoryLetter(item: Pick<WorkItem, "categories">): string {
  const ordered = workCategoriesOrdered(item.categories);
  let comingSoonLetter: string | null = null;

  for (const slug of ordered) {
    const def = WORK_CATEGORY_BY_SLUG[slug];
    if (!def?.serial) {
      continue;
    }
    if (slug === COMING_SOON_CATEGORY_SLUG) {
      comingSoonLetter = def.codeLetter;
      continue;
    }
    return def.codeLetter;
  }

  return comingSoonLetter ?? "?";
}

export type WorkTagCategoryCode = {
  slug: WorkCategorySlug;
  label: string;
  codeLetter: string;
};

export type WorkCategoryCode = {
  slug: WorkCategorySlug;
  label: string;
  codeLetter: string;
  serial: boolean;
};

export function workCategoryCodesForItem(
  item: Pick<WorkItem, "categories">,
): WorkCategoryCode[] {
  return workCategoriesOrdered(item.categories).flatMap((slug) => {
    const def = WORK_CATEGORY_BY_SLUG[slug as WorkCategorySlug];
    if (!def) {
      return [];
    }
    return [
      {
        slug: def.slug,
        label: def.label,
        codeLetter: def.codeLetter,
        serial: def.serial,
      },
    ];
  });
}

/** @deprecated Use workCategoryCodesForItem — tag-only subset. */
export function workTagCategoryCodes(
  item: Pick<WorkItem, "categories">,
): WorkTagCategoryCode[] {
  return workCategoryCodesForItem(item)
    .filter((entry) => !entry.serial)
    .map(({ slug, label, codeLetter }) => ({ slug, label, codeLetter }));
}

export function assertWorkCategoriesComplete(
  items: WorkItem[] = WORK_ITEMS,
): void {
  const letters = new Set<string>();
  for (const entry of WORK_CATEGORIES) {
    if (!entry.codeLetter) {
      throw new Error(`Category "${entry.slug}" is missing a code letter.`);
    }
    if (letters.has(entry.codeLetter)) {
      throw new Error(
        `Duplicate category code letter "${entry.codeLetter}" on "${entry.slug}".`,
      );
    }
    letters.add(entry.codeLetter);
  }

  const newsletterSeriesLetters = new Set<string>([
    ...NEWSLETTER_NUMBERED_SERIES,
    NEWSLETTER_SERIES_RECAP,
  ]);

  for (const entry of WORK_CATEGORIES.filter((item) => item.serial)) {
    if (newsletterSeriesLetters.has(entry.codeLetter)) {
      throw new Error(
        `Serial work category "${entry.slug}" uses "${entry.codeLetter}", which overlaps a newsletter series letter (first character). Use a different letter.`,
      );
    }
  }

  for (const item of items) {
    if (!item.categories?.length) {
      throw new Error(`Work item "${item.id}" has no categories.`);
    }

    for (const slug of item.categories) {
      if (!WORK_CATEGORY_BY_SLUG[slug as WorkCategorySlug]) {
        throw new Error(`Unknown category "${slug}" on work item "${item.id}".`);
      }
    }

    if (
      item.categories.includes(COMING_SOON_CATEGORY_SLUG) &&
      item.categories[item.categories.length - 1] !== COMING_SOON_CATEGORY_SLUG
    ) {
      throw new Error(
        `Work item "${item.id}" must list "${COMING_SOON_CATEGORY_SLUG}" as its last category.`,
      );
    }

    if (workSerialCategoryLetter(item) === "?") {
      throw new Error(
        `Work item "${item.id}" has no serial category for project code assignment.`,
      );
    }
  }
}

export function workCategoriesForApi(): Array<{
  slug: WorkCategorySlug;
  label: string;
  codeLetter: string;
  serial: boolean;
}> {
  return WORK_CATEGORIES.map((entry) => ({
    slug: entry.slug,
    label: entry.label,
    codeLetter: entry.codeLetter,
    serial: entry.serial,
  }));
}
